import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useAllJobs } from "../../../hooks/useJobs";
import type { RuleCellFormat } from "../../team/WageSettings/wageRuleCellText";
import { useBreakdownPanel } from "../BreakdownPanelContext";
import { moneyOrHidden } from "../consts";
import { toCents } from "../wageComposition";

/**
 * How a hover card writes a settings cell: the Wage Settings wording, amounts through the panel's hide
 * rule, and jobs by name from the global catalog (a home department's rule can name a job this department
 * does not offer).
 */
export const useRuleCellFormat = (): RuleCellFormat => {
	const { t } = useTranslation();
	const { canViewWage } = useBreakdownPanel();
	const { data: allJobs } = useAllJobs();
	const { chosenDepartment } = useHospitalData();
	return useMemo(() => {
		const names = new Map([...(allJobs ?? []), ...(chosenDepartment.jobs ?? [])].map((job) => [job.id, job.displayName ?? job.name]));
		return {
			t,
			money: (dollars) => moneyOrHidden(toCents(dollars), canViewWage),
			// DEFAULT: a job the catalog has not loaded (or no longer has) reads as "Specific jobs", which is
			// true of the rule, rather than as its id.
			jobLabel: (jobId) => names.get(jobId) ?? t("wageBreakdown.scope.specificJobs"),
		};
	}, [t, canViewWage, allJobs, chosenDepartment.jobs]);
};
